import { usePlaygroundLocalMsg } from "@faire/prototype-playground/platform/localization";
import { Add } from "@faire/slate-icons/Add";
import { Check } from "@faire/slate-icons/Check";
import { Button } from "@faire/slate/components/button";
import {
  BaseModal,
  BaseModalBody,
  BaseModalFooter,
  BaseModalHeader,
} from "@faire/slate/components/modal/base";
import { tsv } from "@faire/slate/lib/tsv";

import type { FavoriteCollection } from "./FavoriteCollectionPicker";

interface AddToCollectionModalProps {
  collections: FavoriteCollection[];
  open: boolean;
  selectedCollectionIds: string[];
  onClose: () => void;
  onSave: () => void;
  onToggleCollection: (collectionId: string) => void;
}

export const AddToCollectionModal: React.FC<AddToCollectionModalProps> = ({
  collections,
  open,
  selectedCollectionIds,
  onClose,
  onSave,
  onToggleCollection,
}) => {
  const title = usePlaygroundLocalMsg("Add to collection");
  const saveLabel = usePlaygroundLocalMsg("Save");

  return (
    <BaseModal
      data-test-id="add-to-collection-modal"
      onClose={onClose}
      open={open}
      size="small"
    >
      <BaseModalHeader title={title} />
      <BaseModalBody>
        <div className={collectionListStyle.cls()}>
          {collections.map((collection) => (
            <CollectionRow
              collection={collection}
              isSelected={selectedCollectionIds.includes(collection.id)}
              key={collection.id}
              onToggle={onToggleCollection}
            />
          ))}
        </div>
      </BaseModalBody>
      <BaseModalFooter>
        <Button fullWidth onClick={onSave}>
          {saveLabel}
        </Button>
      </BaseModalFooter>
    </BaseModal>
  );
};

interface CollectionRowProps {
  collection: FavoriteCollection;
  isSelected: boolean;
  onToggle: (collectionId: string) => void;
}

const CollectionRow: React.FC<CollectionRowProps> = ({
  collection,
  isSelected,
  onToggle,
}) => {
  const addLabel = usePlaygroundLocalMsg("Add to {collectionName}", {
    collectionName: collection.name,
  });
  const removeLabel = usePlaygroundLocalMsg("Remove from {collectionName}", {
    collectionName: collection.name,
  });

  return (
    <button
      aria-label={isSelected ? removeLabel : addLabel}
      aria-pressed={isSelected}
      className={collectionRowStyle.cls()}
      onClick={() => onToggle(collection.id)}
      type="button"
    >
      <div className={collectionThumbnailStyle.cls()}>
        {collection.imageUrl ? (
          <img
            alt=""
            className="absolute inset-0 size-full object-cover"
            src={collection.imageUrl}
          />
        ) : null}
      </div>
      <p className="type-fs-paragraph-medium min-w-0 flex-1 truncate text-left">
        {collection.name}
      </p>
      <span
        aria-hidden
        className={selectionIndicatorStyle.cls({ isSelected })}
      >
        {isSelected ? (
          <Check
            color="var(--slate-color-icon-primary-inverse)"
            height="16px"
            width="16px"
          />
        ) : (
          <Add height="16px" width="16px" />
        )}
      </span>
    </button>
  );
};

const collectionListStyle = tsv({
  base: ["flex w-full flex-col", "gap-(--slate-spacing-group-large)"],
});

const collectionRowStyle = tsv({
  base: [
    "group focusable interactive rounded-fs-component-default flex min-h-10 w-full items-center",
    "gap-(--slate-spacing-group-medium)",
  ],
});

const collectionThumbnailStyle = tsv({
  base: [
    "bg-fs-surface-tertiary",
    "rounded-fs-component-default relative size-10 shrink-0 overflow-hidden",
  ],
});

const selectionIndicatorStyle = tsv({
  base: [
    "flex size-8 shrink-0 items-center justify-center",
    "rounded-fs-component-round transition-colors",
  ],
  variants: {
    isSelected: {
      true: [
        "bg-fs-action-surface-default text-fs-icon-primary-inverse",
        "group-hover:bg-fs-action-surface-hover",
      ],
      false: [
        "border-fs-action-border-subdued bg-fs-action-surface-inverse border",
        "group-hover:border-fs-action-border-hover group-hover:bg-fs-action-surface-inverse-hover",
      ],
    },
  },
});
